import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config/api';

const SubscribePage = () => {
  const { user } = useAuth();
  const [plans, setPlans] = useState(null);
  const [email, setEmail] = useState('');
  const [selectedPlan, setSelectedPlan] = useState('yearly');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  // Pre-fill email if user is logged in
  useEffect(() => {
    if (user?.email) {
      setEmail(user.email);
    }
  }, [user]);

  useEffect(() => {
    axios.get(`${API_URL}/v1/subscription/plans`)
      .then(res => setPlans(res.data))
      .catch(err => console.error('Failed to load plans', err));
  }, []);

  // If user is already Pro, show a different message
  if (user?.tier === 'pro') {
    return (
      <>
        <Header />
        <main className="main page-content">
          <div className="subscribe-page">
            <div className="already-pro">
              <h1 className="page-title">You're a Pro! 🎉</h1>
              <p className="page-subtitle">Thank you for supporting independent journalism analysis.</p>
              <div className="pro-features-active">
                <h3>Your Pro Features</h3>
                <ul>
                  <li>✓ Daily digest emails</li>
                  <li>✓ Full archive access</li>
                  <li>✓ Priority support</li>
                </ul>
              </div>
              <button className="action-btn primary" onClick={() => navigate('/stories')}>
                Explore Stories
              </button>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const handleCheckout = async () => {
    if (!email) {
      setError('Please enter your email');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const res = await axios.post(`${API_URL}/v1/subscription/checkout`, {
        plan: selectedPlan,
        email: email,
        origin_url: window.location.origin
      });
      
      // Redirect to Stripe
      window.location.href = res.data.checkout_url;
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to start checkout');
      setLoading(false);
    }
  };

  return (
    <>
      <Header />
      <main className="main page-content">
        <div className="subscribe-page">
          <h1 className="page-title">Subscribe to Framecast Pro</h1>
          <p className="page-subtitle">Get daily digest emails and support independent journalism analysis.</p>
          
          {/* Pro Features Preview */}
          <div className="pro-features-preview">
            <div className="feature-item">
              <span className="feature-icon">📧</span>
              <span>Daily Digest Emails</span>
            </div>
            <div className="feature-item">
              <span className="feature-icon">📚</span>
              <span>Full Archive Access</span>
            </div>
            <div className="feature-item">
              <span className="feature-icon">⚡</span>
              <span>Priority Support</span>
            </div>
          </div>
          
          {plans && (
            <div className="plans-grid">
              <div 
                className={`plan-card ${selectedPlan === 'weekly' ? 'selected' : ''}`}
                onClick={() => setSelectedPlan('weekly')}
                data-testid="plan-weekly"
              >
                <h3>Weekly</h3>
                <div className="plan-price">$1<span>/week</span></div>
                <ul>
                  {plans.plans.weekly.features.map((f, i) => (
                    <li key={i}>{f}</li>
                  ))}
                </ul>
              </div>
              
              <div 
                className={`plan-card ${selectedPlan === 'yearly' ? 'selected' : ''}`}
                onClick={() => setSelectedPlan('yearly')}
                data-testid="plan-yearly"
              >
                <div className="plan-badge popular">Most Popular</div>
                <div className="plan-badge savings">Save 23%</div>
                <h3>Yearly</h3>
                <div className="plan-price">$40<span>/year</span></div>
                <ul>
                  {plans.plans.yearly.features.map((f, i) => (
                    <li key={i}>{f}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}
          
          <div className="checkout-form">
            <input 
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="checkout-email"
              data-testid="checkout-email"
            />
            <button 
              className="action-btn primary checkout-btn"
              onClick={handleCheckout}
              disabled={loading}
              data-testid="checkout-btn"
            >
              {loading ? 'Processing...' : `Subscribe ${selectedPlan === 'weekly' ? '$1/week' : '$40/year'}`}
            </button>
            {error && <p className="error-text">{error}</p>}
          </div>
          
          <div className="free-features">
            <h3>Free features (no account needed)</h3>
            <ul>
              <li>Full comparison view</li>
              <li>All stories, all regions</li>
              <li>Today's framing analysis</li>
              <li>Methodology transparency</li>
            </ul>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default SubscribePage;
